import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memberInfoKey } from '@/hooks/member/memberInfoApi';
import { upcomingPaymentsKey } from '../upcomingPayments/useUpcomingPayments';
import {
  requestScheduleOneTimePayment,
  ScheduleOneTimePaymentError,
  type ScheduleOneTimePaymentRequest,
} from './oneTimePaymentApi';

/**
 * Schedules a one-time payment from the payment modal.
 *
 * Exposes `mutateAsync` rather than `mutate` so the modal can await the result: it advances to its
 * success step on resolve, and stays open with the form intact on reject. Invalidates rather than
 * writing an optimistic row, because the modal shows a success screen instead of closing, which
 * gives the refetch time to land before the member sees the table again.
 *
 * The new row also changes the member's balance, so `myMemberInfo` is invalidated alongside it.
 */
export function useScheduleOneTimePayment() {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: requestScheduleOneTimePayment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: upcomingPaymentsKey });
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
    },
    onError: (error: unknown) => {
      const code = error instanceof ScheduleOneTimePaymentError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'scheduleOneTimePayment', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return {
    schedule: (input: ScheduleOneTimePaymentRequest) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}
